import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { UserService } from '../../services/UserService';
import { DoctorService } from '../../services/DoctorService';
import { useAuth } from '../../context/AuthContext';
import { formatPhoneNumber } from '../../utils/format';
import AgendaColorField from '../../components/admin/AgendaColorField';
import { isAgendaRole, normalizeAgendaColor } from '../../utils/agendaColor';


interface CreateUserForm {
  name: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: 'patient' | 'doctor' | 'fisioterapeuta' | 'interno';
  userAssignedCalendarName?: string;
  agendaColor?: string;
  crefito?: string;
  crm?: string;
  accessStartAt?: string;
  accessEndAt?: string;
}

interface CreateUserPageProps {
  onClose: (shouldRefetch?: boolean) => void;
}

const CreateUserPage = ({ onClose }: CreateUserPageProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const { user: adminUser, selectedClient } = useAuth(); // <- DESESTRUTURAR selectedClient aqui
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<CreateUserForm>();
  const password = watch('password');
  const selectedRole = watch('role');
  const activeClientId = selectedClient?.clientId || adminUser?.current_client_id || null;
  const showAgendaColorSettings = isAgendaRole(selectedRole);
  const showInternalAccessSettings = selectedRole === 'interno';
  const showCalendarAssignment = selectedRole === 'fisioterapeuta' || selectedRole === 'interno';

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const numbers = input.replace(/\D/g, '');

    let formatted = '';

    if (numbers.length > 0) {
      if (numbers.length <= 2) {
        formatted = `(${numbers}`;
      } else if (numbers.length <= 6) {
        formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      } else if (numbers.length <= 10) {
        formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6, 10)}`;
      } else {
        formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
      }
    }

    setPhoneValue(formatted);
    setValue('phone', formatted, { shouldValidate: true });
  };

  const onSubmit = async (data: CreateUserForm) => {
    if (!adminUser || !activeClientId || selectedClient?.userRole !== 'admin') {
      toast.error('Apenas administradores podem criar usuários');
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    const normalizedCrefito = data.crefito?.trim() || '';
    if (data.role === 'fisioterapeuta' && !normalizedCrefito) {
      toast.error('Informe o CREFITO do fisioterapeuta');
      return;
    }

    if (data.role === 'interno') {
      if (!data.accessStartAt || !data.accessEndAt) {
        toast.error('Defina o início e o fim do acesso do perfil Interno');
        return;
      }

      if (new Date(data.accessEndAt).getTime() <= new Date(data.accessStartAt).getTime()) {
        toast.error('O fim do acesso deve ser posterior ao início');
        return;
      }
    }

    setIsLoading(true);

    try {
      const calendarName =
        showCalendarAssignment
          ? (data.userAssignedCalendarName || '').trim()
          : '';
      const normalizedAgendaColor = isAgendaRole(data.role)
        ? normalizeAgendaColor(data.agendaColor)
        : null;

      const { user: newUser, isNew } = await UserService.createUser({ 
        userEmail: data.email,
        userDisplayName: data.name,
        userPhoneNumber: formatPhoneNumber(data.phone),
        userAssignedCalendarName:
          calendarName
            ? calendarName
            : undefined,
      }, data.role, activeClientId, data.password, {
        accessStartAt:
          data.role === 'interno' && data.accessStartAt
            ? new Date(data.accessStartAt).toISOString()
            : null,
        accessEndAt:
          data.role === 'interno' && data.accessEndAt
            ? new Date(data.accessEndAt).toISOString()
            : null,
        agendaColor: normalizedAgendaColor,
      });

      if (!newUser) {
        throw new Error("A criação ou associação do usuário falhou.");
      }

      if (!isNew && calendarName) {
        await UserService.updateUser(newUser.userId, {
          userAssignedCalendarName: calendarName,
        });
      }

      if (data.role === 'doctor' || data.role === 'fisioterapeuta') {
        const existingDoctor = await DoctorService.getDoctorByuserId(newUser.userId);
        const professionalData = data.role === 'fisioterapeuta'
          ? { doctorCrefito: normalizedCrefito }
          : { doctorCrm: data.crm?.trim() || '' };

        if (existingDoctor) {
          await DoctorService.updateDoctor(existingDoctor.doctorId, professionalData);
        } else {
          await DoctorService.createDoctor({ userId: newUser.userId, ...professionalData });
        }
      }

      if (isNew) {
        toast.success(
          data.role === 'fisioterapeuta'
            ? 'Fisioterapeuta criado. Configure vínculo, remuneração e regras de repasse em Financeiro > Configurações.'
            : 'Usuário criado com sucesso!'
        );
      } else {
        toast.info(
          data.role === 'fisioterapeuta'
            ? 'Fisioterapeuta associado à clínica. Senha e dados pessoais existentes foram preservados; configure vínculo, remuneração e regras de repasse em Financeiro > Configurações.'
            : calendarName
            ? 'Usuário existente foi associado a esta clínica. Senha e dados pessoais não foram alterados, mas o calendário vinculado foi atualizado.'
            : 'Usuário existente foi associado a esta clínica. No entanto, como o mesmo já possuia acesso ao sistema em outra clínica, a senha e os dados pessoais não foram alterados.'
        );
      }
      reset();
      setPhoneValue('');
      onClose(true); // Envia o sinal para recarregar os dados
    } catch (error: any) {
      console.error('Error creating user:', error);
      toast.error(error.message || 'Erro ao criar usuário');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-6">Criar Novo Usuário</h2>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register('agendaColor')} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <h3 className="md:col-span-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Dados pessoais</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome Completo</label>
            <input
              type="text"
              {...register('name', { required: 'Nome é obrigatório' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              {...register('email', { 
                required: 'Email é obrigatório',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Email inválido'
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Telefone</label>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="(00) 00000-0000"
              value={phoneValue}
              {...register('phone', {
                required: 'Telefone é obrigatório',
                validate: (value) => {
                  const numbers = value.replace(/\D/g, '');
                  if (numbers.length === 10 || numbers.length === 11) {
                    return true;
                  }
                  return 'Telefone deve ter 10 ou 11 dígitos';
                }
              })}
              onChange={handlePhoneChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Perfil nesta clínica</label>
            <select
              {...register('role', { required: 'Perfil na clínica é obrigatório' })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              <option value="patient">Paciente</option>
              <option value="doctor">Médico</option>
              <option value="fisioterapeuta">Fisioterapeuta</option>
              <option value="interno">Interno</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          <h3 className="md:col-span-2 border-t border-gray-200 pt-5 text-sm font-semibold uppercase tracking-wide text-gray-500">Acesso à clínica</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700">Senha</label>
            <input
              type="password"
              {...register('password', { 
                required: 'Senha é obrigatória',
                minLength: {
                  value: 6,
                  message: 'A senha deve ter pelo menos 6 caracteres'
                }
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Confirmar Senha</label>
            <input
              type="password"
              {...register('confirmPassword', {
                required: 'Confirmação de senha é obrigatória',
                validate: value => value === password || 'As senhas não coincidem'
              })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          {selectedRole === 'fisioterapeuta' && (
            <div className="md:col-span-2 grid grid-cols-1 gap-6 border-t border-gray-200 pt-5 md:grid-cols-2">
              <h3 className="md:col-span-2 text-sm font-semibold uppercase tracking-wide text-gray-500">Dados profissionais</h3>
              <div>
              <label className="block text-sm font-medium text-gray-700">CREFITO</label>
              <input
                type="text"
                {...register('crefito', {
                  validate: (value) => selectedRole !== 'fisioterapeuta' || Boolean(value?.trim()) || 'CREFITO é obrigatório',
                })}
                placeholder="Ex: CREFITO-8/123456"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">Informe o registro profissional completo; hífen, espaço e barra são aceitos.</p>
              {errors.crefito && <p className="mt-1 text-sm text-red-600">{errors.crefito.message}</p>}
              </div>
            </div>
          )}

          {selectedRole === 'doctor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">CRM</label>
              <input
                type="text"
                {...register("crm")}
                placeholder="Ex: CRM-SP/123456"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          )}

          {showCalendarAssignment && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Calendário vinculado</label>
              <input
                type="text"
                {...register("userAssignedCalendarName")}
                placeholder={selectedRole === 'fisioterapeuta' ? 'Ex: Agenda - Carolina' : 'Ex: Agenda - Internos'}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          )}

          {showInternalAccessSettings && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Início do acesso</label>
                <input
                  type="datetime-local"
                  {...register("accessStartAt", {
                    validate: (value) =>
                      watch("role") !== "interno" || Boolean(value) || 'Informe o início do acesso',
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {errors.accessStartAt && (
                  <p className="mt-1 text-sm text-red-600">{errors.accessStartAt.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fim do acesso</label>
                <input
                  type="datetime-local"
                  {...register("accessEndAt", {
                    validate: (value) => {
                      if (watch("role") !== "interno") return true;
                      if (!value) return 'Informe o fim do acesso';
                      const start = watch("accessStartAt");
                      if (start && new Date(value).getTime() <= new Date(start).getTime()) {
                        return 'O fim do acesso deve ser posterior ao início';
                      }
                      return true;
                    },
                  })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
                {errors.accessEndAt && (
                  <p className="mt-1 text-sm text-red-600">{errors.accessEndAt.message}</p>
                )}
              </div>
            </>
          )}

        </div>

        {selectedRole === 'fisioterapeuta' && (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            O cadastro profissional não ativa repasses. Depois de criar o usuário, configure o vínculo, o modelo de remuneração, a vigência e os valores em <strong>Financeiro &gt; Configurações</strong>.
          </div>
        )}

        {showAgendaColorSettings && (
          <div className="border-t border-gray-200 pt-6">
            <AgendaColorField
              value={watch('agendaColor') || ''}
              onChange={(value) => setValue('agendaColor', value, { shouldDirty: true })}
            />
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={() => onClose()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Voltar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isLoading ? 'Criando...' : 'Criar Usuário'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateUserPage;
